/**
 * Correcting one of the four retained fields.
 *
 * Winnie, 18 September, on the two things that decide whether a correction is allowed and
 * whether it needs chasing afterwards. Both are pure arithmetic over values the caller
 * already holds, so they live here rather than inside the service — the form, the service and
 * the QA suite all read the same rule.
 */

export type CorrectableField = 'sale_price' | 'sold_date' | 'purchase_price' | 'purchase_date'

export const CORRECTABLE_FIELDS: CorrectableField[] = [
  'sale_price',
  'sold_date',
  'purchase_price',
  'purchase_date',
]

export const FIELD_LABEL: Record<CorrectableField, string> = {
  sale_price: 'Sale price',
  sold_date: 'Sold date',
  purchase_price: 'Purchase price',
  purchase_date: 'Purchase date',
}

/** Which side of the deal a field belongs to — it decides which date tests the lock. */
export const isSaleSideField = (f: CorrectableField): boolean =>
  f === 'sale_price' || f === 'sold_date'

export const isMoneyField = (f: CorrectableField): boolean =>
  f === 'sale_price' || f === 'purchase_price'

export type CorrectorRole = 'Admin' | 'SuperAdmin'

export interface LockVerdict {
  /** May this correction be saved at all? */
  allowed: boolean
  /** Is the effective date inside a closed accounting period? */
  lockedPeriod: boolean
  /** True only when a Super Admin is reaching into a closed period. */
  bypassedLock: boolean
  /** The refusal, in the words Winnie wrote, when allowed is false. */
  message: string
}

const day = (v?: string | null): string => String(v || '').slice(0, 10)

/**
 * May this person correct this field, given when the deal happened and where the books are
 * closed to?
 *
 * "Two tiers, one requirement that never changes": an Admin corrects freely while the period
 * is open and is blocked the moment it closes; a Super Admin bypasses the lock entirely. The
 * reason is mandatory either way — that is enforced by the caller and by the table, not here,
 * because it is not a question about the lock.
 *
 * A deal with no effective date cannot be inside a closed period, so it is treated as open.
 * Refusing it instead would block correcting the very field — a missing sold date — that
 * somebody is most likely trying to fix.
 */
export function lockVerdict(args: {
  field: CorrectableField
  /** sold_date for sale-side fields, purchase_date for purchase-side fields. */
  effectiveDate?: string | null
  accountingLockDate?: string | null
  role: CorrectorRole
}): LockVerdict {
  const lock = day(args.accountingLockDate)
  const effective = day(args.effectiveDate)
  const lockedPeriod = !!lock && !!effective && effective <= lock

  if (lockedPeriod && args.role === 'Admin') {
    return {
      allowed: false,
      lockedPeriod: true,
      bypassedLock: false,
      message:
        'This falls within a locked accounting period. Contact a Super Admin to make this ' +
        'correction, or an administrator to adjust the lock date.',
    }
  }

  return {
    allowed: true,
    lockedPeriod,
    bypassedLock: lockedPeriod && args.role === 'SuperAdmin',
    message: '',
  }
}

export interface Materiality {
  variancePct: number
  varianceDollar: number
  material: boolean
}

/**
 * Does this correction need picking up on the next BAS?
 *
 * "Materiality threshold: 10% or $100, whichever is met." Winnie names the reason for the
 * pair explicitly: a percentage alone flags trivial swings on small figures, a dollar amount
 * alone misses meaningful percentage swings on large ones.
 *
 * Only ever asked about a bypassed-lock correction. An ordinary Admin correction can only
 * touch an open period, which by definition has not been lodged, so there is nothing to
 * revise.
 *
 * A date correction has no dollar variance of its own but can still move a credit into a
 * different BAS period, which is exactly the case that needs a person to look at it — so a
 * changed date is always material.
 */
export function assessMateriality(
  field: CorrectableField,
  previousValue: unknown,
  correctedValue: unknown,
): Materiality {
  if (!isMoneyField(field)) {
    const changed = day(String(previousValue ?? '')) !== day(String(correctedValue ?? ''))
    return { variancePct: 0, varianceDollar: 0, material: changed }
  }

  const before = Number(previousValue) || 0
  const after = Number(correctedValue) || 0
  const varianceDollar = Math.abs(after - before)
  /* Against the original figure, because that is the one that was reported. A zero original
     with a non-zero correction is an infinite percentage swing, so it is material on the
     dollar test instead of producing NaN. */
  const variancePct = before === 0 ? (varianceDollar > 0 ? 1 : 0) : varianceDollar / Math.abs(before)

  return {
    variancePct,
    varianceDollar,
    material: variancePct > 0.1 || varianceDollar > 100,
  }
}

/** What has to be recalculated once this field is corrected. */
export function downstreamOf(field: CorrectableField): {
  commission: boolean
  marginScheme: boolean
  arAmountDue: boolean
  basPeriod: boolean
} {
  return {
    commission: isMoneyField(field),
    marginScheme: isMoneyField(field),
    arAmountDue: field === 'sale_price',
    basPeriod: !isMoneyField(field),
  }
}
